import { CHECKOUT_DATA } from '@/constants/queries';
import { apiClient } from '@/services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { Routes } from './routes';
import type { CheckoutData } from '@/types/checkout';
import { getRedirectLinkFromUrlParams } from '@/utils/params';
import { extractRedirectUrlFromApiError, persistRedirectUrl } from '@/utils/persistedRedirectUrl';
import { consumePrefetch } from './checkoutDataPrefetch';

/**
 * Hoisted rather than built per render: `checkoutData.data` is used in
 * dependency arrays, so a fresh object each render would thrash them.
 */
const EMPTY_CHECKOUT_DATA = {} as CheckoutData;

export const useCheckoutData = ({ enableFetch }: { enableFetch: boolean }) => {
  const query = useQuery({
    queryKey: [CHECKOUT_DATA],
    enabled: enableFetch,
    // The queryFn is attempted exactly once, as it was when it swallowed its
    // own failures. Retries would delay the error screen and the fatal SDK
    // event by the full backoff sequence.
    retry: false,
    // A rejected query (ACDEV-28760) never stores successful data, and in
    // TanStack Query v5 a query with no data is ALWAYS stale — even under the
    // global `staleTime: Infinity`. The default focus/reconnect refetch would
    // therefore re-issue `/checkout/v3` after a session failure and replace
    // `.error` (re-firing the fatal SDK event). Pin them off to keep the
    // one-shot / fail-fast contract intact for the error path too.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<CheckoutData> => {
      // Perf: if `main.tsx` already kicked off `/checkout/v3` at module-
      // eval time, consume that promise instead of issuing a second GET.
      // One-shot — refetches/invalidations bypass this and go through
      // `apiClient` for fresh data. A prefetch error falls through to
      // `apiClient` so a transient pre-flight failure still gets a retry.
      const prefetched = consumePrefetch();
      if (prefetched) {
        try {
          const checkoutData = await prefetched;
          persistRedirectUrl(checkoutData.redirectUrl ?? (getRedirectLinkFromUrlParams() || undefined));
          return checkoutData;
        } catch (error) {
          persistRedirectUrl(extractRedirectUrlFromApiError(error));
          // fall through to apiClient — same recovery shape as the
          // pre-existing try/catch below.
        }
      }

      try {
        const response = await apiClient.get(Routes.CHECKOUT);
        const checkoutData = response as CheckoutData;
        persistRedirectUrl(checkoutData.redirectUrl ?? (getRedirectLinkFromUrlParams() || undefined));
        return checkoutData;
      } catch (error) {
        persistRedirectUrl(extractRedirectUrlFromApiError(error));
        // ACDEV-28760: re-throw so the query REJECTS and the gateway errorCode
        // stays reachable on `.error` for the fatal SDK events (see the
        // `query.isError` mapping below). Do NOT swallow to `{}` here.
        throw error;
      }
    },
  });

  // Callers read `.data` unconditionally and expect `{}` — never `undefined` —
  // once a request has failed. It must stay `undefined` while pending, though:
  // `initilizeCheckoutData` gates on `!checkoutData.data` to avoid initializing
  // against a payload that has not arrived yet.
  //
  // Letting the query reject (instead of resolving `{}`) is what keeps the
  // gateway `errorCode` reachable on `.error` for the fatal SDK events.
  return { ...query, data: query.isError ? EMPTY_CHECKOUT_DATA : query.data };
};
